import { useCallback, useEffect, useState } from "react";
import { Link, useNavigate, useParams } from "react-router-dom";
import { toast } from "sonner";
import { ArrowLeft, ClipboardCheck, MinusCircle, Pencil, Settings2, Trash2 } from "lucide-react";
import { Button } from "@/components/ui/button";
import { api, errMsg } from "@/lib/api";
import { fmtDate, fmtEUR, fmtQty } from "@/lib/format";
import { useAuth } from "@/context/AuthContext";
import { ConfirmDialog, Loading, PageHeader, btnGhost, btnPrimary } from "@/components/app/common";
import { ImpostazioniDialog, RettificaDialog } from "@/components/app/MagazzinoDialogs";
import { StatoBadge } from "@/pages/Magazzino";
import { ProdottoDialog } from "@/components/app/ProdottoDialog";
import { EliminaReferenzaDialog, RimuoviDialog } from "@/components/app/MagazzinoAzioni";
import { useMarche } from "@/components/app/MarcaSelect";

const Kpi = ({ label, value, testId, tone = "" }) => (
  <div className="card-soft px-5 py-4" data-testid={testId}><div className="text-[11px] uppercase tracking-wider text-[#756B64] font-semibold">{label}</div><div className={`text-xl font-semibold tabular mt-1 ${tone}`}>{value}</div></div>
);

function LedgerRow({ m, i, onDelete }) {
  const doc = m.fattura_id ? <Link to={`/fatture/${m.fattura_id}`} className="underline decoration-dotted hover:text-[#8C6D62]">{m.documento}</Link> : m.documento;
  return (
    <tr data-testid={`ledger-row-${i}`} className="hover:bg-[#FAF7F5]">
      <td className="px-4 py-3 tabular">{fmtDate(m.data)}</td>
      <td className="px-4 py-3 font-medium">{m.tipo}</td>
      <td className="px-4 py-3 text-[#756B64] max-w-[320px] truncate">{doc}</td>
      <td className={`px-4 py-3 text-right tabular font-semibold ${m.quantita >= 0 ? "text-[#4E6656]" : "text-[#A2513C]"}`} data-testid={`ledger-qty-${i}`}>{m.quantita > 0 ? "+" : ""}{fmtQty(m.quantita)}</td>
      <td className={`px-4 py-3 text-right tabular ${m.saldo < 0 ? "text-[#B4553D] font-semibold" : ""}`} data-testid={`ledger-saldo-${i}`}>{fmtQty(m.saldo)}</td>
      <td className="px-2 py-3 text-right">{m.rettifica_id && <button data-testid={`ledger-delete-${i}`} onClick={() => onDelete(m)} className="p-2 rounded-lg hover:bg-[#E07A5F]/10 text-[#B4553D]" title="Elimina rettifica"><Trash2 className="h-4 w-4" /></button>}</td>
    </tr>
  );
}

export default function MagazzinoDettaglio() {
  const { id } = useParams();
  const navigate = useNavigate();
  const { isAdmin } = useAuth();
  const [data, setData] = useState(null);
  const [dlg, setDlg] = useState(null);
  const [del, setDel] = useState(null);
  const [edit, setEdit] = useState(null);
  const marche = useMarche();
  const openEdit = async () => {
    try {
      const [{ data: prods }, { data: lin }] = await Promise.all([api.get("/prodotti"), api.get("/categorie", { params: { tipo: "linea" } })]);
      const it = prods.find((x) => x.id === id);
      setEdit({ item: { ...it, ean: it.ean || "", sku: it.sku || "", marca_id: it.marca_id || "" }, linee: lin });
    } catch (e) { toast.error(errMsg(e)); }
  };
  const load = useCallback(() => api.get(`/magazzino/${id}`).then((r) => setData(r.data)).catch((e) => toast.error(errMsg(e))), [id]);
  useEffect(() => { load(); }, [load]);
  const remove = async () => {
    try { await api.delete(`/magazzino/rettifiche/${del.rettifica_id}`); toast.success("Rettifica eliminata"); load(); } catch (e) { toast.error(errMsg(e)); }
    setDel(null);
  };
  if (!data) return <Loading />;
  const p = data.prodotto;
  const movs = [...data.movimenti].reverse();

  return (
    <div data-testid="magazzino-dettaglio">
      <button onClick={() => navigate("/magazzino")} className="text-sm text-[#756B64] hover:text-[#2C2623] inline-flex items-center gap-1 mb-4" data-testid="back-to-magazzino"><ArrowLeft className="h-4 w-4" />Magazzino</button>
      <PageHeader overline={`${p.marca || "Senza marca"} · ${p.sku || "Senza SKU"} · ${p.linea}`} title={p.nome} subtitle="Tutti i movimenti di magazzino con saldo progressivo.">
        <StatoBadge r={p} />
        {isAdmin && <Button variant="outline" className={btnGhost} onClick={openEdit} data-testid="dettaglio-edit-button"><Pencil className="h-4 w-4 mr-1" />Modifica</Button>}
        {isAdmin && <Button variant="outline" className={btnGhost} onClick={() => setDlg("rimuovi")} data-testid="dettaglio-rimuovi-button"><MinusCircle className="h-4 w-4 mr-1" />Rimuovi quantità</Button>}
        {isAdmin && <Button variant="outline" className={`${btnGhost} text-[#B4553D]`} onClick={() => setDlg("elimina")} data-testid="dettaglio-elimina-button"><Trash2 className="h-4 w-4 mr-1" />Elimina referenza</Button>}
        <Button variant="outline" className={btnGhost} onClick={() => setDlg("imp")} data-testid="impostazioni-button"><Settings2 className="h-4 w-4 mr-1" />Giacenza iniziale e scorta</Button>
        <Button className={btnPrimary} onClick={() => setDlg("rett")} data-testid="rettifica-button"><ClipboardCheck className="h-4 w-4 mr-1" />Rettifica inventario</Button>
      </PageHeader>
      <div className="grid grid-cols-2 lg:grid-cols-4 gap-3 mb-6">
        <Kpi label="Giacenza attuale" value={`${fmtQty(p.giacenza)} pz`} testId="dettaglio-giacenza" tone={p.negativa ? "text-[#B4553D]" : ""} />
        {isAdmin && <Kpi label="Valore giacenza" value={fmtEUR(p.valore)} testId="dettaglio-valore" />}
        {isAdmin && <Kpi label="Costo medio pond." value={fmtEUR(p.costo_medio)} testId="dettaglio-costo-medio" />}
        <Kpi label="Scorta minima" value={p.scorta_minima != null ? `${fmtQty(p.scorta_minima)} pz` : "—"} testId="dettaglio-scorta" />
        <Kpi label="Giacenza iniziale" value={`${fmtQty(p.giacenza_iniziale)} pz`} testId="dettaglio-giacenza-iniziale" />
      </div>
      <div className="card-soft overflow-x-auto" data-testid="ledger-table">
        <table className="w-full text-sm min-w-[760px]">
          <thead className="bg-[#FAF7F5] text-[11px] uppercase tracking-wider text-[#756B64]">
            <tr>{["Data", "Tipo", "Documento", "Movimento", "Saldo", ""].map((h, i) => <th key={i} className={`font-semibold px-4 py-3 ${i > 2 ? "text-right" : "text-left"}`}>{h}</th>)}</tr>
          </thead>
          <tbody className="divide-y divide-[#EAE3DC]">
            {!movs.length ? <tr><td colSpan={6} className="px-4 py-10 text-center text-[#756B64]">Nessun movimento di magazzino.</td></tr> : movs.map((m, i) => <LedgerRow key={i} m={m} i={i} onDelete={setDel} />)}
          </tbody>
        </table>
      </div>
      <ProdottoDialog open={!!edit} item={edit?.item} linee={edit?.linee || []} marche={marche} onOpenChange={(o) => !o && setEdit(null)} onSaved={load} />
      <RimuoviDialog prodotto={dlg === "rimuovi" ? { ...p, id } : null} onClose={() => setDlg(null)} onDone={(d) => setData({ prodotto: d.prodotto, movimenti: d.movimenti })} />
      <EliminaReferenzaDialog prodotto={dlg === "elimina" ? { ...p, id } : null} onClose={() => setDlg(null)} onDone={() => navigate("/magazzino")} />
      <ImpostazioniDialog open={dlg === "imp"} onOpenChange={(o) => !o && setDlg(null)} prodotto={p} onSaved={setData} />
      <RettificaDialog open={dlg === "rett"} onOpenChange={(o) => !o && setDlg(null)} prodotto={p} onSaved={(d) => setData({ prodotto: d.prodotto, movimenti: d.movimenti })} />
      <ConfirmDialog open={!!del} onOpenChange={(o) => !o && setDel(null)} title="Eliminare la rettifica?" text="La giacenza verrà ricalcolata senza questa rettifica." onConfirm={remove} />
    </div>
  );
}
